import apiClient from './apiClient';

export interface FinancialSummary {
  totalDueCents: number;
  paidAmountCents: number;
  remainingCents: number;
  status: 'unpaid' | 'partial' | 'paid';
  payments: { id: string; amount: number; currency: string; status: string; provider: string; createdAt: string; receiptUrl?: string }[];
  refunds: { id: string; amount: number; status: string; createdAt: string }[];
}

export interface CreateIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  publishableKey?: string;
  paymentId: string;
}

export interface PaymentSettings {
  id?: string;
  provider: string;
  enabled: boolean;
  mode: 'test' | 'live';
  publishableKey: string;
  hasSecret?: boolean;
  hasWebhookSecret?: boolean;
  allowPartial: boolean;
  allowRefunds: boolean;
  /** true când setările sunt gestionate de master admin; clinic_admin vede doar statusul */
  managedByMaster?: boolean;
}

export interface WebhookEventRow {
  id: string;
  event_id: string;
  event_type: string;
  status: string;
  error_message: string | null;
  processed_at: string;
}

class ClinicPaymentsService {
  async getFinancialSummary(visitId: string): Promise<FinancialSummary> {
    const { data } = await apiClient.get<{ success: boolean; data: FinancialSummary }>(`/visits/${visitId}/financial-summary`);
    return data.data;
  }

  async createIntent(visitId: string, invoiceId: string, amountCents?: number): Promise<CreateIntentResponse> {
    const { data } = await apiClient.post<{ success: boolean; data: CreateIntentResponse }>('/clinic-payments/stripe/create-intent', {
      visit_id: visitId,
      invoice_id: invoiceId,
      amount_cents: amountCents
    });
    return data.data;
  }

  async refund(paymentId: string, amount?: number, reason?: string): Promise<{ refundId: string; providerRefundId: string; status: string }> {
    const { data } = await apiClient.post<{ success: boolean; data: { refundId: string; providerRefundId: string; status: string } }>(
      `/clinic-payments/${paymentId}/refund`,
      { amount, reason }
    );
    return data.data;
  }

  async getPaymentSettings(): Promise<PaymentSettings> {
    const { data } = await apiClient.get<{ success: boolean; data: PaymentSettings }>('/clinic-payments/settings');
    return data.data;
  }

  async updatePaymentSettings(
    settings: Partial<PaymentSettings> & { secretKey?: string; webhookSecret?: string; clinic_id?: string }
  ): Promise<void> {
    await apiClient.put('/clinic-payments/settings', {
      clinic_id: settings.clinic_id,
      enabled: settings.enabled,
      mode: settings.mode,
      publishableKey: settings.publishableKey,
      secretKey: settings.secretKey,
      webhookSecret: settings.webhookSecret,
      allowPartial: settings.allowPartial,
      allowRefunds: settings.allowRefunds
    });
  }

  async getPaymentSettingsForClinic(clinicId: string): Promise<PaymentSettings> {
    const { data } = await apiClient.get<{ success: boolean; data: PaymentSettings }>('/clinic-payments/settings', {
      params: { clinicId }
    });
    return data.data;
  }

  async testPaymentConfig(clinicId?: string): Promise<void> {
    await apiClient.post('/clinic-payments/settings/test', clinicId ? { clinic_id: clinicId } : undefined);
  }

  async getWebhookEvents(limit?: number, clinicId?: string): Promise<WebhookEventRow[]> {
    const params: Record<string, number | string> = { limit: limit ?? 50 };
    if (clinicId) params.clinicId = clinicId;
    const { data } = await apiClient.get<{ success: boolean; data: WebhookEventRow[] }>('/clinic-payments/webhook-events', {
      params
    });
    return data.data;
  }

  async retryPayment(paymentId: string): Promise<CreateIntentResponse> {
    const { data } = await apiClient.post<{ success: boolean; data: CreateIntentResponse }>(
      `/clinic-payments/${paymentId}/retry`,
      {}
    );
    return data.data;
  }

  async reconcile(opts?: { apply_safe_fixes?: boolean; payment_ids?: string[]; limit?: number }): Promise<{
    checked: number;
    updated: number;
    mismatches: Array<{
      paymentId: string;
      providerPaymentId: string;
      localStatus: string | null;
      providerStatus: string;
      message: string;
      autoFixApplied?: boolean;
    }>;
  }> {
    const { data } = await apiClient.post<{ success: boolean; data: any }>('/clinic-payments/reconcile', opts ?? {});
    return data.data;
  }
}

export const clinicPaymentsService = new ClinicPaymentsService();
